// Post page with comments. Sending is optimistic: the comment shows at once as
// "đang gửi…", becomes real when the API answers, and is rolled back on error.
// New comments from others arrive through the realtime stream.

import { h, toast } from '../dom';
import { api, type Comment, type Post, type User } from '../../api/client';
import { avatar, confirmBox, guard, section, skeleton, timeAgo } from '../components';
import { postCard } from '../post';
import { shell } from '../shell';
import { navigate, queryParam } from '../router';
import { openReactors } from '../reactors';
import { onPush, setCommentCount } from '../live';

type Row = Comment & { pending?: boolean };

export function renderPost(root: HTMLElement, me: User, id: string) {
  const content = shell(root, { title: 'Bài đăng', back: '#/feed' });
  const box = h('div.comments', { 'aria-live': 'polite' }, skeleton('row', 2));
  const heading = h('h3', {}, 'Bình luận');
  let rows: Row[] = [];
  let loaded = false;
  let post: Post | null = null;
  let replyTo: Comment | null = null;
  const replyHint = h('div.replyhint', { hidden: true });
  const input = h('textarea.field', { rows: 1, maxLength: 2000, placeholder: 'Viết bình luận…', 'aria-label': 'Bình luận' });
  const send = h('button.primary.small', { disabled: true }, 'Gửi');
  input.oninput = () => (send.disabled = !input.value.trim());

  const count = () => rows.filter((r) => !r.pending).length;
  const syncCount = (n = count()) => {
    heading.textContent = n ? `Bình luận (${n})` : 'Bình luận';
    setCommentCount(id, n);
    if (post) post.commentCount = n;
  };

  const paint = () => {
    const top = rows.filter((c) => !c.parentId);
    const children = (pid: string) => rows.filter((c) => c.parentId === pid);
    const row = (c: Row, child = false): HTMLElement => {
      const mine = c.author.id === me.id;
      const actions = h('div.cactions', {},
        h('small', {}, c.pending ? 'Đang gửi…' : timeAgo(c.createdAt), c.editedAt ? ' · đã sửa' : ''),
        c.pending ? null : h('button.link', { onclick: () => setReply(c) }, 'Trả lời'),
        mine && !c.pending ? h('button.link', {
          onclick: async () => {
            const text = prompt('Sửa bình luận', c.body);
            if (!text || !text.trim() || text === c.body) return;
            const upd = await guard(api.editComment(c.id, text));
            if (upd) { Object.assign(c, upd); paint(); }
          },
        }, 'Sửa') : null,
        (mine || post?.author.id === me.id) && !c.pending ? h('button.link', { onclick: () => void remove(c) }, 'Xoá') : null);
      return h('div.comment', { class: [child ? 'reply' : '', c.pending ? 'pending' : ''].join(' '), 'data-comment-id': c.id },
        h('a', { href: `#/u/${c.author.id}`, 'aria-label': c.author.displayName }, avatar(c.author, child ? 28 : 34)),
        h('div.cmain', {}, h('a.pname', { href: `#/u/${c.author.id}` }, c.author.displayName), h('p.cbody', {}, c.body), actions,
          ...(child ? [] : children(c.id).map((r) => row(r, true)))));
    };
    box.replaceChildren(...(top.length ? top.map((c) => row(c)) : [h('p.tiny.center', {}, 'Chưa có bình luận. Hãy là người đầu tiên ♡')]));
  };

  /** Server list + our still-pending ones (they are not on the server yet). */
  const load = async () => {
    const list = await api.comments(id);
    rows = [...list, ...rows.filter((r) => r.pending)];
    loaded = true;
    paint();
    syncCount();
  };

  const setReply = (c: Comment | null) => {
    // replies are one level deep: answering a reply goes to its thread, mentioning the person
    const parent = c?.parentId ? rows.find((r) => r.id === c.parentId) ?? c : c;
    replyTo = parent;
    replyHint.hidden = !c;
    replyHint.replaceChildren(c ? h('span', {}, `Trả lời ${c.author.displayName}`) : '', h('button.link', { onclick: () => setReply(null) }, 'Huỷ'));
    if (c) {
      if (c.parentId && !input.value.startsWith('@')) input.value = `@${c.author.displayName} ` + input.value;
      input.focus();
      send.disabled = !input.value.trim();
    }
  };

  const submit = async () => {
    const text = input.value.trim();
    if (!text) return;
    const parent = replyTo;
    const temp: Row = {
      id: `tmp-${Math.random().toString(36).slice(2)}`, postId: id, author: me, parentId: parent?.id ?? null,
      body: text, createdAt: new Date().toISOString(), editedAt: null, pending: true,
    };
    rows.push(temp);
    input.value = '';
    send.disabled = true;
    setReply(null);
    paint();
    syncCount(count() + 1);
    try {
      const c = await api.comment(id, text, parent?.id);
      const i = rows.indexOf(temp);
      if (rows.some((r) => r.id === c.id)) rows.splice(i, 1); // the realtime refresh got it first
      else rows[i] = c;
    } catch (e) {
      rows.splice(rows.indexOf(temp), 1);
      if (!input.value) input.value = text; // give the text back
      send.disabled = !input.value.trim();
      toast(`Chưa gửi được bình luận: ${(e as Error).message}`, 3500);
    }
    paint();
    syncCount();
  };

  const remove = async (c: Row) => {
    if (!(await confirmBox('Xoá bình luận này?', 'Xoá'))) return;
    const before = rows;
    rows = rows.filter((r) => r.id !== c.id && r.parentId !== c.id);
    paint();
    syncCount();
    try {
      await api.deleteComment(c.id);
    } catch (e) {
      rows = before;
      paint();
      syncCount();
      toast((e as Error).message, 3500);
    }
  };

  send.onclick = () => void submit();
  input.addEventListener('keydown', (e) => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); void submit(); } });

  // a push about this post (new comment / reply) → refresh the list (one request)
  const off = onPush((m) => {
    const d = m.data ?? {};
    if (!loaded || (d.postId !== id && (d.url ?? '').split('?')[0] !== `#/post/${id}`)) return;
    void load().catch(() => {});
  });

  content.append(
    section(() => api.post(id), (p) => {
      post = p;
      void load().catch((e) => box.replaceChildren(h('p.tiny', {}, (e as Error).message)));
      // from a REACTION notification: show who reacted right away (once)
      if (queryParam('reactors') === '1') {
        history.replaceState(null, '', `#/post/${id}`);
        setTimeout(() => openReactors(id, p.reactions), 0);
      }
      return postCard(p, me, { detail: true, eager: true, onDeleted: () => navigate('#/feed') });
    }, () => skeleton('post', 1)),
    h('section.commentbox', {}, heading, box, replyHint, h('div.cinput', {}, input, send)),
  );
  return () => off();
}
